import { Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnDestroy, Output } from '@angular/core';

export interface DescriptionDropdownOption {
  value: any;
  label: string;
  description: string;
  disabled?: boolean;
  icon?: string;
  color?: string;
  isClientAction?: boolean;
  // Only used when the dropdown is given `categories`: the key(s) of the category (or categories) this option is listed
  // under. An option can be in more than one, and one with none is listed directly in the top-level menu.
  category?: string | string[];
}

export interface DescriptionDropdownCategory {
  key: string;
  label: string;
  description?: string;
  icon?: string;
}

@Component({
  selector: 'app-description-dropdown',
  templateUrl: './description-dropdown.component.html',
  styleUrls: ['./description-dropdown.component.scss']
})
export class DescriptionDropdownComponent implements OnDestroy {
  @Input() options: DescriptionDropdownOption[] = [];
  @Input() value: any;
  @Output() valueChange = new EventEmitter<any>();
  @Input() disabled = false;
  @Input() placeholder = 'Select an option';
  // Optional. When set the menu first lists these categories; hovering (or clicking) one opens a second menu to its right
  // with that category's options. Leave it out and the dropdown is the plain flat list it always was.
  @Input() categories: DescriptionDropdownCategory[] = [];
  // Floating: the menu is placed against the window (position: fixed) instead of inside the dropdown, so a
  // scrolling or clipping parent - e.g. a table cell - can't cut it off. It opens below the dropdown, or above
  // when there's more room there, and follows the dropdown if the page scrolls while it's open.
  @Input() floating = false;

  isOpen = false;
  activeCategory: string | null = null;

  constructor(private elementRef: ElementRef, private zone: NgZone) {}

  private floatFrame: number | null = null;
  private lastTriggerRect: DOMRect | null = null;
  private readonly FLOAT_GAP = 2;
  private readonly FLOAT_MARGIN = 8;
  private readonly FLOAT_MIN_WIDTH = 280;

  private startFloating() {
    this.lastTriggerRect = null;
    // Outside Angular so following the dropdown every frame doesn't run change detection every frame
    this.zone.runOutsideAngular(() => {
      const follow = () => {
        if (!this.isOpen || !this.floating) { this.floatFrame = null; return; }
        this.positionFloatingPanel();
        this.floatFrame = requestAnimationFrame(follow);
      };
      this.floatFrame = requestAnimationFrame(follow);
    });
  }

  private stopFloating() {
    if (this.floatFrame !== null) cancelAnimationFrame(this.floatFrame);
    this.floatFrame = null;
  }

  private positionFloatingPanel() {
    const host = this.elementRef.nativeElement as HTMLElement;
    const trigger = host.querySelector('.desc-dd__trigger') as HTMLElement | null;
    const panel = host.querySelector('.desc-dd__panel') as HTMLElement | null;
    if (!trigger || !panel) return;
    const t = trigger.getBoundingClientRect();
    const last = this.lastTriggerRect;
    if (last && last.top === t.top && last.left === t.left && last.width === t.width && last.height === t.height) return;
    this.lastTriggerRect = t;

    const width = Math.max(t.width, this.FLOAT_MIN_WIDTH);
    const left = Math.max(this.FLOAT_MARGIN, Math.min(t.left, window.innerWidth - width - this.FLOAT_MARGIN));
    const spaceBelow = window.innerHeight - t.bottom - this.FLOAT_MARGIN - this.FLOAT_GAP;
    const spaceAbove = t.top - this.FLOAT_MARGIN - this.FLOAT_GAP;
    const openUp = spaceBelow < 250 && spaceAbove > spaceBelow;

    const style = panel.style;
    style.position = 'fixed';
    style.right = 'auto';
    style.width = `${width}px`;
    style.maxHeight = `${Math.max(120, openUp ? spaceAbove : spaceBelow)}px`;
    style.overflowY = 'auto';
    // Place it at 0,0 first and see where that actually lands: inside a transformed ancestor (e.g. an Ionic
    // modal) position: fixed is relative to that ancestor rather than the window, so shift by the difference.
    style.top = '0px';
    style.left = '0px';
    const origin = panel.getBoundingClientRect();
    const top = openUp ? t.top - this.FLOAT_GAP - origin.height : t.bottom + this.FLOAT_GAP;
    style.top = `${top - origin.top}px`;
    style.left = `${left - origin.left}px`;
  }

  get selectedOption(): DescriptionDropdownOption | undefined {
    return this.options.find(o => o.value === this.value);
  }

  get hasCategories(): boolean {
    return !!this.categories && this.categories.length > 0;
  }

  optionCategories(option: DescriptionDropdownOption): string[] {
    if (option.category === undefined || option.category === null) return [];
    return Array.isArray(option.category) ? option.category : [option.category];
  }

  optionsInCategory(key: string): DescriptionDropdownOption[] {
    return this.options.filter(o => this.optionCategories(o).includes(key));
  }

  // Options that aren't in any (known) category stay in the top-level menu so they can never disappear
  get uncategorizedOptions(): DescriptionDropdownOption[] {
    const known = new Set(this.categories.map(c => c.key));
    return this.options.filter(o => !this.optionCategories(o).some(k => known.has(k)));
  }

  categoryIsDisabled(key: string): boolean {
    const inCategory = this.optionsInCategory(key);
    return inCategory.length === 0 || inCategory.every(o => o.disabled);
  }

  categoryHasSelected(key: string): boolean {
    return this.optionsInCategory(key).some(o => o.value === this.value);
  }

  // Where the open category's menu sits relative to the top of the panel. It starts level with the top; if that would run
  // off the bottom of the visible area it is slid up (and only limited in height if it can't fit at all).
  submenuOffset = 0;
  submenuMaxHeight: number | null = null;
  submenuReady = false;
  private readonly SUBMENU_MARGIN = 8;

  private closeTimer: any = null;

  private cancelCloseCategory() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  // Closing waits a moment, so a pointer that drifts slightly off the row or menu on its way across doesn't lose the menu.
  // Moving onto any category (or back onto the menu) cancels it.
  // Called from (mouseleave), which runs outside Angular (mouseenter/mouseleave are left unpatched in zone-flags.ts so
  // hovering doesn't re-check the whole app) - so it steps back into Angular itself.
  scheduleCloseCategory() {
    this.zone.run(() => {
      this.cancelCloseCategory();
      if (this.lockedCategory) return; // a clicked category stays put until something is picked or the dropdown is closed
      this.closeTimer = setTimeout(() => {
        this.activeCategory = null;
        this.closeTimer = null;
      }, 150);
    });
  }

  ngOnDestroy() {
    this.cancelCloseCategory();
    this.stopFloating();
  }

  private categoryRow(key: string): HTMLElement | null {
    const host = this.elementRef.nativeElement as HTMLElement;
    return host.querySelector(`[data-category="${key}"] .desc-dd__category-row`) as HTMLElement | null;
  }

  // Hovering opens a category's menu, and moving away closes it - until a category is clicked. A clicked category is locked
  // open: hovering elsewhere or moving off no longer changes anything, so the options never move under the user. It stays
  // until an option is picked, the category is clicked again, or the dropdown is closed.
  lockedCategory: string | null = null;

  // Hover - from (mouseenter), which runs outside Angular (see scheduleCloseCategory)
  openCategory(key: string) {
    this.zone.run(() => {
      if (this.lockedCategory) return;
      this.showCategory(key);
    });
  }

  private showCategory(key: string) {
    this.cancelCloseCategory();
    if (this.categoryIsDisabled(key) || this.activeCategory === key) return;
    this.activeCategory = key;
    // The menu starts level with its own category row, so moving straight across always lands on it (a short menu would
    // otherwise sit at the top of the panel, well above a row near the bottom).
    this.submenuOffset = this.categoryRow(key)?.offsetTop ?? 0;
    this.submenuMaxHeight = null;
    this.submenuReady = false;
    // Once the menu has rendered at that spot it can be measured and, if needed, moved up
    setTimeout(() => this.positionSubmenu());
  }

  // Click: lock this category open (clicking the locked one again unlocks it, going back to hover behaviour; clicking a
  // different one moves the lock to it)
  toggleCategory(key: string) {
    if (this.categoryIsDisabled(key)) return;
    if (this.lockedCategory === key) {
      this.lockedCategory = null;
      return;
    }
    this.lockedCategory = key;
    this.showCategory(key);
  }

  // Parent in the rendered tree - crosses slots and shadow roots, so it reaches Ionic's scrolling content area
  private renderedParent(node: HTMLElement): HTMLElement | null {
    return (node.assignedSlot as HTMLElement | null)
      ?? node.parentElement
      ?? (((node.getRootNode() as ShadowRoot)?.host as HTMLElement | undefined) ?? null);
  }

  // The visible area the menu has to stay inside: the window, narrowed by any scrolling/clipping ancestor (e.g. a modal's content)
  private visibleBounds(el: HTMLElement): { top: number; bottom: number } {
    let top = 0;
    let bottom = window.innerHeight;
    let node = this.renderedParent(el);
    while (node && node !== document.body) {
      const overflowY = getComputedStyle(node).overflowY;
      if (overflowY === 'hidden' || overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'clip') {
        const rect = node.getBoundingClientRect();
        top = Math.max(top, rect.top);
        bottom = Math.min(bottom, rect.bottom);
      }
      node = this.renderedParent(node);
    }
    return { top, bottom };
  }

  private positionSubmenu() {
    const host = this.elementRef.nativeElement as HTMLElement;
    const submenu = host.querySelector('.desc-dd__submenu') as HTMLElement | null;
    const panel = host.querySelector('.desc-dd__panel') as HTMLElement | null;
    if (!submenu || !panel) return;

    const bounds = this.visibleBounds(panel);
    const minTop = bounds.top + this.SUBMENU_MARGIN;
    const maxBottom = bounds.bottom - this.SUBMENU_MARGIN;
    const height = submenu.getBoundingClientRect().height;
    const panelTop = panel.getBoundingClientRect().top;
    const rowTop = this.submenuOffset; // where it was started: level with its category row

    if (height > maxBottom - minTop) {
      // Can't fit even using all the room - use it all and scroll inside the menu
      this.submenuMaxHeight = Math.max(0, maxBottom - minTop);
      this.submenuOffset = minTop - panelTop;
    } else if (panelTop + rowTop + height > maxBottom) {
      // Slide up just enough to clear the bottom, but never above the top of the visible area. It still overlaps the row
      // (the row is visible, so it sits above the bottom edge the menu is aligned to).
      this.submenuOffset = Math.max(maxBottom - height - panelTop, minTop - panelTop);
    }
    this.submenuReady = true;
  }

  toggle() {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (!this.isOpen) this.resetCategories();
    else if (this.floating) this.startFloating();
  }

  private resetCategories() {
    this.stopFloating();
    this.cancelCloseCategory();
    this.activeCategory = null;
    this.lockedCategory = null;
  }

  select(option: DescriptionDropdownOption) {
    if (option.disabled) return;
    this.value = option.value;
    this.valueChange.emit(option.value);
    this.isOpen = false;
    this.resetCategories();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
      this.resetCategories();
    }
  }
}
